import { API_URL } from '../config'
import { readSessionTokens } from './session'
import { isPublicPath } from './publicPages'
import {
  createSessionLimitError,
  isSessionOverLimit,
  reportSessionLimitReached
} from './sessionLimit'

const defaultHeaders = {
  accept: 'application/json',
  'content-type': 'application/json'
}

/**
 * Reads the access token of the current Cognito session.
 *
 * Amplify renews the token with the refresh token when it has expired, so the
 * token we get here is always valid while the session lives.
 *
 * When nobody is signed in there is no token. That is normal for the public
 * pages and the sign up, so the request goes out without one. Amplify also
 * gives no token when the session has ended, and then the listener in index.js
 * sends the user to the login page.
 *
 * Once the session passes SESSION_MAX_AGE after the sign in, we report the
 * limit, so the listener ends the session. A public page works without a
 * session, so its request goes out without a token. On any other page the
 * request does not go out.
 *
 * Public pages also send no token if renewal fails, so a Cognito outage does
 * not prevent a shared link from loading.
 *
 * @returns {Promise<string|undefined>} The JWT, or undefined when there is none.
 * @throws The SESSION_LIMIT_ERROR error when the session is over the limit,
 *   outside the public pages.
 * @throws On private pages, when renewal fails, such as on a network error.
 *   Amplify keeps the session then, so we fail the request instead of sending
 *   it without a token. Without a token the API would answer 401, and once the
 *   API sends CORS headers on its errors, the polling sagas would send the user
 *   to the login page even though the session is still valid.
 */
async function getAccessToken() {
  let tokens
  try {
    tokens = await readSessionTokens()
  } catch (err) {
    if (isPublicPath(window.location.pathname)) return undefined
    throw err
  }

  if (isSessionOverLimit(tokens)) {
    reportSessionLimitReached()
    if (isPublicPath(window.location.pathname)) return undefined
    throw createSessionLimitError()
  }

  return tokens?.accessToken?.toString()
}

/**
 * Builds the headers for one request.
 *
 * The caller's headers are added on top of the defaults, so a caller that only
 * changes accept still sends the token.
 *
 * @param {Object} [headers] - Extra headers from the caller.
 * @returns {Promise<Object>} A new headers object for this request.
 */
async function buildHeaders(headers) {
  const token = await getAccessToken()
  const result = { ...defaultHeaders, ...headers }

  if (token) {
    result.Authorization = `Bearer ${token}`
  }

  return result
}

/**
 * Builds the error for a response that is not ok.
 *
 * 401 and 403 keep the message 'Unauthorized', which the polling sagas check.
 * Both mean the session is not valid: our API authorizer answers an expired or
 * wrong token with 403, and API Gateway answers a request with no token with
 * 401. A resource the user cannot see is a 404, not a 403. Every error keeps res and status, because the public pages read them to tell
 * a forbidden channel from a missing one.
 *
 * @param {Response} res - The fetch response.
 * @returns {Error} The error to throw.
 */
function buildResponseError(res) {
  const isUnauthorized = res.status === 401 || res.status === 403
  const message = isUnauthorized
    ? 'Unauthorized'
    : res.statusText || `Request failed with status ${res.status}`
  const error = new Error(message)
  error.res = res
  error.status = res.status
  return error
}

export default async function request(path, args = {}) {
  const url = `${API_URL}/${path}`

  const options = {
    headers: await buildHeaders(args.headers),
    method: args.method || 'GET'
  }

  if (args.data) {
    options.body = JSON.stringify(args.data)
    options.headers['content-type'] = 'application/json'
  }
  return fetch(url, options).then(res => {
    if (res.status === 204) {
      return res
    }
    if (res.status >= 200 && res.status < 300) {
      return res.json().then(body => [body, res])
    }
    throw buildResponseError(res)
  })
}

export async function requestFile(path, args = {}) {
  const url = `${API_URL}/${path}`

  const options = {
    headers: await buildHeaders(args.headers),
    method: args.method || 'GET'
  }

  if (args.data) {
    options.body = JSON.stringify(args.data)
    options.headers['content-type'] = 'application/json'
  }
  return fetch(url, options).then(res => {
    if (res.status >= 200 && res.status < 300) {
      return res
    }
    if (res.status === 413) {
      return 413
    }
    return Promise.reject(buildResponseError(res))
  })
}

export function base64ToBlob(base64, mime = 'application/octet-stream') {
  const binary = atob(base64)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i)
  }
  return new Blob([bytes], { type: mime })
}

export function downloadPdfFromPayload(payload, defaultFilename) {
  if (!payload?.pdf) {
    return Promise.reject(new Error('Missing PDF payload'))
  }
  const blob = base64ToBlob(payload.pdf, 'application/pdf')
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = `${payload.filename || defaultFilename}.pdf`
  link.click()
  URL.revokeObjectURL(url)
  return Promise.resolve({ worked: 'worked' })
}

export function downloadPdfFromBase64Response(response, defaultFilename, parsedPayload = null) {
  const worked = response.headers.get('reult')
  if (worked !== 'worked') {
    return Promise.resolve({ worked })
  }

  const legacyFileUrl = response.headers.get('fileUrl')
  if (legacyFileUrl) {
    const link = document.createElement('a')
    link.href = legacyFileUrl
    link.download = `${defaultFilename}.pdf`
    link.click()
    return Promise.resolve({ worked: 'worked' })
  }

  const payloadPromise = parsedPayload ? Promise.resolve(parsedPayload) : response.json()

  return payloadPromise.then(payload => downloadPdfFromPayload(payload, defaultFilename))
}
